import React, { useState, useEffect, useRef } from "react";
import {
    X,
    Save,
    Globe,
    Key,
    Loader2,
    Workflow,
    Shield,
    Lock,
    ChevronDown,
    Check,
    Eye,
    EyeOff,
    Send,
    AlertCircle,
    Settings,
    ArrowLeft,
    Plus,
    Trash2,
    RefreshCw,
    Clock,
    CheckCircle2,
    Sliders
} from "lucide-react";
import integrationapi from "../../../services/integrationapi";
import { useClientLevel } from "../../../hooks/useClientLevel";
import { showError, showSuccess } from "../../../utils/toast";

interface WebhookIntegrationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    integrationData?: any;
}

interface DropdownOption {
    value: string;
    label: string;
    icon?: React.ComponentType<{ className?: string }>;
    badge?: string;
}

interface CustomDropdownProps {
    value: string;
    onChange: (value: string) => void;
    options: DropdownOption[];
    disabled?: boolean;
    label?: string;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
    value,
    onChange,
    options,
    disabled = false,
    label
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const selectedOption = options.find(opt => opt.value === value) || options[0];

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="relative" ref={dropdownRef}>
            {label && (
                <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                    {label}
                </label>
            )}
            <button
                type="button"
                disabled={disabled}
                onClick={() => setIsOpen(!isOpen)}
                className={`w-full bg-gray-50 dark:bg-[#0D1117] border rounded-xl px-3.5 py-2.5 text-left flex items-center justify-between transition-all outline-none ${isOpen ? 'border-orange-500 ring-1 ring-orange-500/20' : 'border-gray-300 dark:border-gray-800 hover:border-gray-400 dark:hover:border-gray-700'
                    } ${disabled ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
            >
                <div className="flex items-center gap-2.5 min-w-0 pr-2">
                    {selectedOption?.icon && (
                        <div className="w-6 h-6 rounded-md bg-gray-200/80 dark:bg-gray-800/80 border border-gray-300/80 dark:border-gray-700/60 flex items-center justify-center shrink-0 text-orange-500 dark:text-orange-400">
                            <selectedOption.icon className="w-3.5 h-3.5" />
                        </div>
                    )}
                    <span className="text-xs font-semibold text-gray-800 dark:text-gray-200 truncate">{selectedOption?.label}</span>
                    {selectedOption?.badge && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20 shrink-0">
                            {selectedOption.badge}
                        </span>
                    )}
                </div>
                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 dark:text-gray-500 transition-transform duration-200 shrink-0 ${isOpen ? 'rotate-180 text-orange-500 dark:text-orange-400' : ''}`} />
            </button>

            {isOpen && !disabled && (
                <div className="absolute z-50 left-0 right-0 mt-1.5 bg-white dark:bg-[#0E131F] border border-gray-200 dark:border-gray-700/80 rounded-xl shadow-2xl py-1 overflow-hidden animate-in fade-in zoom-in-95 duration-150 backdrop-blur-xl">
                    {options.map((opt) => {
                        const isSelected = opt.value === value;
                        const Icon = opt.icon;
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => {
                                    onChange(opt.value);
                                    setIsOpen(false);
                                }}
                                className={`w-full px-3.5 py-2 flex items-center justify-between text-left transition-colors cursor-pointer ${isSelected
                                    ? 'bg-orange-500/10 text-orange-600 dark:text-orange-300'
                                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/60 hover:text-gray-900 dark:hover:text-white'
                                    }`}
                            >
                                <div className="flex items-center gap-2.5 min-w-0 pr-2">
                                    {Icon && (
                                        <div className={`w-6 h-6 rounded-md border flex items-center justify-center shrink-0 ${isSelected
                                            ? 'bg-orange-500/20 border-orange-500/40 text-orange-600 dark:text-orange-400'
                                            : 'bg-gray-100 dark:bg-gray-800/60 border-gray-200 dark:border-gray-700/50 text-gray-500 dark:text-gray-400'
                                            }`}>
                                            <Icon className="w-3 h-3" />
                                        </div>
                                    )}
                                    <span className="text-xs font-medium truncate">{opt.label}</span>
                                    {opt.badge && (
                                        <span className="text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-orange-500/15 text-orange-600 dark:text-orange-400 border border-orange-500/30 shrink-0">
                                            {opt.badge}
                                        </span>
                                    )}
                                </div>
                                {isSelected && (
                                    <Check className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400 shrink-0 ml-2" />
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

interface CustomHeaderRow {
    id: string;
    key: string;
    value: string;
}

interface TestConnectionResult {
    success: boolean;
    statusCode?: number;
    durationMs?: number;
    message: string;
    responsePreview?: string;
}

const HTTP_METHOD_OPTIONS: DropdownOption[] = [
    { value: "POST", label: "POST" },
    { value: "PUT", label: "PUT" },
    { value: "PATCH", label: "PATCH" }
];

const AUTH_TYPE_OPTIONS: DropdownOption[] = [
    { value: "none", label: "None (Open)", icon: Globe },
    { value: "bearer", label: "Bearer Token", icon: Key },
    { value: "api_key", label: "API Key Header", icon: Key },
    // { value: "basic", label: "Basic Auth", icon: Lock },
    { value: "oauth2_client_credentials", label: "OAuth2 Token Exchange", icon: Shield, badge: "Dynamic" }
];

const TOKEN_AUTH_METHODS: DropdownOption[] = [
    { value: "client_secret_post", label: "Form POST (client_secret_post)" },
    { value: "client_secret_basic", label: "Basic Auth Header (client_secret_basic)" },
    { value: "json_post", label: "JSON Body (json_post)" }
];

const TOKEN_PLACEMENTS: DropdownOption[] = [
    { value: "header", label: "HTTP Header" },
    { value: "query_param", label: "URL Query Parameter" }
];

const SIGNATURE_FORMATS: DropdownOption[] = [
    { value: "hex", label: "Raw Hex (e3b0c44...)" },
    { value: "sha256_prefix", label: "Prefixed Hex (sha256=e3b0...)" },
    { value: "base64", label: "Base64 (47DEQpj8...)" }
];

const WebhookIntegrationModal: React.FC<WebhookIntegrationModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    integrationData
}) => {
    const level = useClientLevel();
    const isEditing = !!integrationData;

    // Tabs
    const [activeTab, setActiveTab] = useState<"configuration" | "test">("configuration");

    // Loading states
    const [isLoading, setIsLoading] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [testResult, setTestResult] = useState<TestConnectionResult | null>(null);

    // Password visibility toggles
    const [showBearerToken, setShowBearerToken] = useState(false);
    const [showApiKey, setShowApiKey] = useState(false);
    const [showBasicPassword, setShowBasicPassword] = useState(false);
    const [showClientSecret, setShowClientSecret] = useState(false);
    const [showHmacSecret, setShowHmacSecret] = useState(false);

    // Collapsible sections
    const [showCustomHeaders, setShowCustomHeaders] = useState(false);
    const [showAdvancedOauth, setShowAdvancedOauth] = useState(false);

    // Form fields
    const [webhookUrl, setWebhookUrl] = useState("");
    const [httpMethod, setHttpMethod] = useState("POST");
    const [authType, setAuthType] = useState("none");

    // Auth specific fields
    const [authHeaderName, setAuthHeaderName] = useState("");
    const [authHeaderValue, setAuthHeaderValue] = useState("");
    const [basicUsername, setBasicUsername] = useState("");
    const [basicPassword, setBasicPassword] = useState("");

    // OAuth2 fields
    const [tokenUrl, setTokenUrl] = useState("");
    const [clientId, setClientId] = useState("");
    const [clientSecret, setClientSecret] = useState("");
    const [scope, setScope] = useState("");
    const [tokenAuthMethod, setTokenAuthMethod] = useState("client_secret_post");
    const [tokenResponsePath, setTokenResponsePath] = useState("");
    const [tokenPlacement, setTokenPlacement] = useState("header");
    const [tokenHeaderName, setTokenHeaderName] = useState("Authorization");
    const [tokenFormat, setTokenFormat] = useState("Bearer ");
    const [tokenQueryParam, setTokenQueryParam] = useState("access_token");

    // Custom headers
    const [customHeaders, setCustomHeaders] = useState<CustomHeaderRow[]>([]);

    // HMAC signing
    const [enableHmac, setEnableHmac] = useState(false);
    const [secretToken, setSecretToken] = useState("");
    const [signatureHeader, setSignatureHeader] = useState("X-Webhook-Signature");
    const [signatureFormat, setSignatureFormat] = useState("hex");

    // TLS verification
    const [insecureSkipVerify, setInsecureSkipVerify] = useState(false);

    // Initialize or reset form
    useEffect(() => {
        if (isOpen) {
            setActiveTab("configuration");
            setIsTesting(false);
            setTestResult(null);

            setShowBearerToken(false);
            setShowApiKey(false);
            setShowBasicPassword(false);
            setShowClientSecret(false);
            setShowHmacSecret(false);
            setShowAdvancedOauth(false);

            if (isEditing && integrationData?.integration_data) {
                const data = typeof integrationData.integration_data === 'string'
                    ? JSON.parse(integrationData.integration_data)
                    : integrationData.integration_data;

                setWebhookUrl(data.webhook_url || "");
                setHttpMethod(data.http_method || "POST");

                // Infer auth_type if legacy or absent
                const resolvedAuthType = data.auth_type || (
                    data.basic_username || data.basic_password ? "basic" :
                        data.token_url || data.client_secret ? "oauth2_client_credentials" :
                            data.auth_header_name ? "api_key" :
                                data.auth_header_value ? "bearer" : "none"
                );
                setAuthType(resolvedAuthType);

                setAuthHeaderName(data.auth_header_name || (resolvedAuthType === "api_key" ? "X-API-Key" : ""));
                setAuthHeaderValue(data.auth_header_value || "");
                setBasicUsername(data.basic_username || "");
                setBasicPassword(data.basic_password || "");

                setTokenUrl(data.token_url || "");
                setClientId(data.client_id || "");
                setClientSecret(data.client_secret || "");
                setScope(data.scope || "");
                setTokenAuthMethod(data.token_auth_method || "client_secret_post");
                setTokenResponsePath(data.token_response_path || "");
                setTokenPlacement(data.token_placement || "header");
                setTokenHeaderName(data.token_header_name || "Authorization");
                setTokenFormat(data.token_format !== undefined ? data.token_format : "Bearer ");
                setTokenQueryParam(data.token_query_param || "access_token");

                // Parse custom headers
                if (data.custom_headers && typeof data.custom_headers === "object") {
                    const headerRows = Object.entries(data.custom_headers).map(([key, value], idx) => ({
                        id: `hdr-${idx}-${Date.now()}`,
                        key,
                        value: String(value)
                    }));
                    setCustomHeaders(headerRows);
                    if (headerRows.length > 0) {
                        setShowCustomHeaders(true);
                    }
                } else {
                    setCustomHeaders([]);
                    setShowCustomHeaders(false);
                }

                // HMAC signing
                const hasHmac = !!(data.secret_token || data.signature_header || data.signature_format);
                setEnableHmac(hasHmac);
                setSecretToken(data.secret_token || "");
                setSignatureHeader(data.signature_header || "X-Webhook-Signature");
                setSignatureFormat(data.signature_format || "hex");

                // TLS
                setInsecureSkipVerify(!!data.insecure_skip_verify);
            } else {
                // Reset to defaults
                setWebhookUrl("");
                setHttpMethod("POST");
                setAuthType("none");
                setAuthHeaderName("X-API-Key");
                setAuthHeaderValue("");
                setBasicUsername("");
                setBasicPassword("");
                setTokenUrl("");
                setClientId("");
                setClientSecret("");
                setScope("");
                setTokenAuthMethod("client_secret_post");
                setTokenResponsePath("");
                setTokenPlacement("header");
                setTokenHeaderName("Authorization");
                setTokenFormat("Bearer ");
                setTokenQueryParam("access_token");
                setCustomHeaders([]);
                setShowCustomHeaders(false);
                setEnableHmac(false);
                setSecretToken("");
                setSignatureHeader("X-Webhook-Signature");
                setSignatureFormat("hex");
                setInsecureSkipVerify(false);
            }
            setIsLoading(false);
        }
    }, [isOpen, isEditing, integrationData]);

    if (!isOpen) return null;

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Custom Header helpers
    const handleAddHeader = () => {
        setCustomHeaders(prev => [
            ...prev,
            { id: `hdr-${Date.now()}-${Math.random()}`, key: "", value: "" }
        ]);
        setShowCustomHeaders(true);
    };

    const handleRemoveHeader = (id: string) => {
        setCustomHeaders(prev => prev.filter(h => h.id !== id));
    };

    const handleHeaderChange = (id: string, field: "key" | "value", text: string) => {
        setCustomHeaders(prev => prev.map(h => h.id === id ? { ...h, [field]: text } : h));
    };

    // Validation
    const validateSettings = (): string | null => {
        const trimmedUrl = webhookUrl.trim();
        if (!trimmedUrl) {
            return "Endpoint URL is required";
        }
        try {
            const parsed = new URL(trimmedUrl);
            if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
                return "Webhook URL must start with http:// or https://";
            }
        } catch {
            return "Please enter a valid HTTP/HTTPS URL";
        }

        if (authType === "bearer") {
            if (!authHeaderValue.trim()) {
                return "Please enter the Bearer Token";
            }
        } else if (authType === "api_key") {
            if (!authHeaderName.trim()) {
                return "Please enter the API Key Header Name (e.g. X-API-Key)";
            }
            if (!authHeaderValue.trim()) {
                return "Please enter the API Key Value";
            }
        } else if (authType === "basic") {
            if (!basicUsername.trim()) {
                return "Please enter the Basic Auth Username";
            }
            if (!basicPassword.trim()) {
                return "Please enter the Basic Auth Password";
            }
        } else if (authType === "oauth2_client_credentials") {
            if (!tokenUrl.trim()) {
                return "Please enter the OAuth2 Token URL";
            }
            try {
                const parsedToken = new URL(tokenUrl.trim());
                if (parsedToken.protocol !== "http:" && parsedToken.protocol !== "https:") {
                    return "Token URL must start with http:// or https://";
                }
            } catch {
                return "Please enter a valid OAuth2 Token URL";
            }
            if (!clientId.trim()) {
                return "Please enter the OAuth2 Client ID";
            }
            if (!clientSecret.trim()) {
                return "Please enter the OAuth2 Client Secret";
            }
        }

        if (enableHmac) {
            if (!secretToken.trim()) {
                return "Please provide an HMAC Signing Secret or uncheck HMAC signing";
            }
            if (!signatureHeader.trim()) {
                return "Please enter the Signature Header Name";
            }
        }

        return null;
    };

    // Build API payload
    const buildPayload = () => {
        const payload: any = {
            webhook_url: webhookUrl.trim(),
            http_method: httpMethod,
            auth_type: authType,
            insecure_skip_verify: insecureSkipVerify
        };

        if (authType === "bearer") {
            payload.auth_header_value = authHeaderValue.trim();
        } else if (authType === "api_key") {
            payload.auth_header_name = authHeaderName.trim();
            payload.auth_header_value = authHeaderValue.trim();
        } else if (authType === "basic") {
            payload.basic_username = basicUsername.trim();
            payload.basic_password = basicPassword;
        } else if (authType === "oauth2_client_credentials") {
            payload.token_url = tokenUrl.trim();
            payload.client_id = clientId.trim();
            payload.client_secret = clientSecret;
            if (scope.trim()) payload.scope = scope.trim();
            if (tokenAuthMethod) payload.token_auth_method = tokenAuthMethod;
            if (tokenResponsePath.trim()) payload.token_response_path = tokenResponsePath.trim();
            if (tokenPlacement) payload.token_placement = tokenPlacement;
            if (tokenPlacement === "header") {
                if (tokenHeaderName.trim()) payload.token_header_name = tokenHeaderName.trim();
                if (tokenFormat !== undefined) payload.token_format = tokenFormat;
            } else if (tokenPlacement === "query_param") {
                if (tokenQueryParam.trim()) payload.token_query_param = tokenQueryParam.trim();
            }
        }

        // Custom headers mapping
        const headersMap: Record<string, string> = {};
        customHeaders.forEach(({ key, value }) => {
            const k = key.trim();
            if (k) {
                headersMap[k] = value;
            }
        });
        if (Object.keys(headersMap).length > 0) {
            payload.custom_headers = headersMap;
        }

        // HMAC
        if (enableHmac) {
            payload.secret_token = secretToken;
            payload.signature_header = signatureHeader.trim() || "X-Webhook-Signature";
            payload.signature_format = signatureFormat || "hex";
        }

        return payload;
    };

    const getClientParams = () => {
        const client_id = (level && level.name !== 'global' && level.id)
            ? level.id
            : (integrationData?.client_id || undefined);
        return client_id ? { client_id } : {};
    };

    // Live Test Connection
    const executeTestConnection = async () => {
        const error = validateSettings();
        if (error) {
            showError(error);
            setActiveTab("configuration");
            return;
        }

        setIsTesting(true);
        setTestResult(null);

        try {
            const payload = buildPayload();
            const params = getClientParams();

            const response = await integrationapi.post("integrations/webhook/test", payload, { params });
            const data = response.data;

            const isSuccess = data?.status === "success" || data?.status === true || (data?.status_code >= 200 && data?.status_code < 300);
            const msg = data?.message || (isSuccess ? "Webhook test connection successful" : "Webhook test returned unexpected response");

            setTestResult({
                success: isSuccess,
                statusCode: data?.status_code || response.status || 200,
                durationMs: data?.duration_ms,
                message: msg,
                responsePreview: typeof data?.response_preview === "object"
                    ? JSON.stringify(data.response_preview, null, 2)
                    : data?.response_preview
            });

            if (isSuccess) {
                showSuccess(data?.duration_ms ? `✓ Connected Successfully (${data.duration_ms}ms)` : "Webhook test connection successful");
            } else {
                showError(msg);
            }
        } catch (err: any) {
            console.error("Webhook test failed:", err);
            const errData = err.response?.data;
            const statusCode = errData?.status_code || err.response?.status;
            const durationMs = errData?.duration_ms;
            const errMsg = errData?.error || errData?.message || err.message || "Webhook test connection failed";
            const preview = typeof errData?.response_preview === "object"
                ? JSON.stringify(errData.response_preview, null, 2)
                : (errData?.response_preview || errData?.detail);

            setTestResult({
                success: false,
                statusCode,
                durationMs,
                message: errMsg,
                responsePreview: preview
            });
            showError(errMsg);
        } finally {
            setIsTesting(false);
        }
    };

    // Transition to Test tab only from Test Connection button
    const handleTriggerTestTab = () => {
        const error = validateSettings();
        if (error) {
            showError(error);
            return;
        }
        setActiveTab("test");
        // Immediately run test upon switching
        executeTestConnection();
    };

    // Save configuration
    const handleSave = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();

        const error = validateSettings();
        if (error) {
            showError(error);
            setActiveTab("configuration");
            return;
        }

        setIsLoading(true);
        try {
            const payload = buildPayload();
            const params = getClientParams();

            let response;
            try {
                response = await integrationapi.post("integrations/webhook", payload, { params });
            } catch (postErr: any) {
                if (postErr.response?.status === 404 || postErr.response?.status === 405) {
                    // Fallback to legacy endpoint if backend hasn't mounted new route
                    response = await integrationapi.patch("integrations/webhook/settings", payload, { params });
                } else {
                    throw postErr;
                }
            }

            if (response.data.status) {
                showSuccess(`Webhook integration ${isEditing ? 'updated' : 'installed'} successfully`);
                onSuccess();
                onClose();
            } else {
                showError(response.data.message || "Operation failed");
            }
        } catch (error: any) {
            console.error("Webhook save failed:", error);
            showError(error.response?.data?.message || error.response?.data?.error || error.message || "An error occurred");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 dark:bg-black/80 backdrop-blur-md animate-in fade-in duration-300"
            onClick={handleBackdropClick}
        >
            <div className="bg-white dark:bg-[#111827] border border-gray-200 dark:border-gray-700/60 rounded-2xl w-full max-w-2xl shadow-2xl animate-in zoom-in duration-300 relative flex flex-col max-h-[92vh] overflow-hidden">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-800/80 flex items-center justify-between shrink-0 bg-gray-50/50 dark:bg-[#0F141F]">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
                            <Workflow className="text-orange-500 w-4 h-4" />
                        </div>
                        <div>
                            <h2 className="text-base font-bold text-gray-900 dark:text-white leading-tight">
                                {isEditing ? 'Update Webhook Integration' : 'Configure Webhook Integration'}
                            </h2>
                            <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                Send real-time notifications & scan findings to external endpoints
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-700 dark:hover:text-white p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors cursor-pointer"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Read-only banner if global */}
                {integrationData?.editable === false && (
                    <div className="mx-6 mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl text-blue-600 dark:text-blue-400 text-xs flex items-center gap-2 shrink-0">
                        <Globe className="w-4 h-4 shrink-0" />
                        <span>This global integration is managed centrally. Settings are read-only.</span>
                    </div>
                )}

                {/* Tab Navigation (Direct click to Test is restricted per requirement) */}
                <div className="px-6 pt-3 shrink-0 border-b border-gray-200 dark:border-gray-800/80 bg-gray-50/80 dark:bg-[#0D1117]/50">
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            onClick={() => setActiveTab("configuration")}
                            className={`flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-t-lg transition-all border-b-2 cursor-pointer ${activeTab === "configuration"
                                ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-orange-500/10'
                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800/40'
                                }`}
                        >
                            <Settings className="w-3.5 h-3.5" />
                            Configuration
                        </button>

                        <button
                            type="button"
                            disabled={activeTab !== "test"}
                            onClick={() => {
                                // Direct tab switching to test is restricted unless user triggers via Test button
                            }}
                            title={activeTab !== "test" ? "Click 'Test Connection' button below to test" : "Test Connection"}
                            className={`flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-t-lg transition-all border-b-2 ${activeTab === "test"
                                ? 'border-orange-500 text-orange-600 dark:text-orange-400 bg-orange-500/10 cursor-default'
                                : 'border-transparent text-gray-400 dark:text-gray-500 cursor-not-allowed opacity-60'
                                }`}
                        >
                            <Send className="w-3.5 h-3.5" />
                            Test Connection
                            {testResult && (
                                <span className={`w-2 h-2 rounded-full ${testResult.success ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                            )}
                        </button>
                    </div>
                </div>

                {/* Modal Body */}
                <div className="p-6 overflow-y-auto flex-1 custom-scrollbar">
                    {activeTab === "configuration" ? (
                        <div className="space-y-4">
                            {/* Endpoint URL & Method */}
                            <div>
                                <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                    Endpoint URL <span className="text-orange-500">*</span>
                                </label>
                                <div className="flex gap-2">
                                    <div className="w-28 shrink-0">
                                        <CustomDropdown
                                            value={httpMethod}
                                            onChange={setHttpMethod}
                                            options={HTTP_METHOD_OPTIONS}
                                            disabled={integrationData?.editable === false}
                                        />
                                    </div>
                                    <div className="relative flex-1 group">
                                        <Globe className="absolute left-3.5 top-3 h-3.5 w-3.5 text-gray-400 dark:text-gray-500 group-focus-within:text-orange-500 transition-colors" />
                                        <input
                                            type="url"
                                            disabled={integrationData?.editable === false}
                                            value={webhookUrl}
                                            onChange={(e) => setWebhookUrl(e.target.value)}
                                            className="w-full bg-gray-50 dark:bg-[#0D1117] border border-gray-300 dark:border-gray-800 rounded-xl pl-9 pr-4 py-2.5 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500/50 outline-none transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                                            placeholder="https://api.yourdomain.com/webhooks/listener"
                                            required
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Authentication Method */}
                            <div>
                                <CustomDropdown
                                    label="Authentication Method"
                                    value={authType}
                                    onChange={setAuthType}
                                    options={AUTH_TYPE_OPTIONS}
                                    disabled={integrationData?.editable === false}
                                />
                            </div>

                            {/* Dynamic Auth Inputs */}
                            {authType === "none" && (
                                <div className="p-3 bg-gray-100 dark:bg-gray-800/30 border border-gray-200 dark:border-gray-800 rounded-xl text-gray-600 dark:text-gray-400 text-xs flex items-center gap-2">
                                    <Globe className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400 shrink-0" />
                                    <span>Outbound webhook requests will be delivered with no authentication header.</span>
                                </div>
                            )}

                            {authType === "bearer" && (
                                <div className="p-3.5 bg-gray-50/80 dark:bg-[#0D1117]/60 border border-gray-200 dark:border-gray-800/80 rounded-xl space-y-2">
                                    <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block px-0.5">
                                        Bearer Token <span className="text-orange-500">*</span>
                                    </label>
                                    <div className="relative group">
                                        <Key className="absolute left-3.5 top-3 h-3.5 w-3.5 text-gray-400 dark:text-gray-500 group-focus-within:text-orange-500 transition-colors" />
                                        <input
                                            type={showBearerToken ? "text" : "password"}
                                            disabled={integrationData?.editable === false}
                                            value={authHeaderValue}
                                            onChange={(e) => setAuthHeaderValue(e.target.value)}
                                            autoComplete="new-password"
                                            placeholder="ey... or Bearer token"
                                            className="w-full bg-gray-50 dark:bg-[#0D1117] border border-gray-300 dark:border-gray-800 rounded-xl pl-9 pr-10 py-2.5 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500/50 outline-none transition-all"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowBearerToken(!showBearerToken)}
                                            className="absolute right-3 top-2.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-0.5"
                                        >
                                            {showBearerToken ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5 text-gray-400" />}
                                        </button>
                                    </div>
                                </div>
                            )}

                            {authType === "api_key" && (
                                <div className="p-3.5 bg-gray-50/80 dark:bg-[#0D1117]/60 border border-gray-200 dark:border-gray-800/80 rounded-xl space-y-3">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                            <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                                Header Name <span className="text-orange-500">*</span>
                                            </label>
                                            <div className="relative group">
                                                <Key className="absolute left-3.5 top-3 h-3.5 w-3.5 text-gray-400 dark:text-gray-500 group-focus-within:text-orange-500 transition-colors" />
                                                <input
                                                    type="text"
                                                    disabled={integrationData?.editable === false}
                                                    value={authHeaderName}
                                                    onChange={(e) => setAuthHeaderName(e.target.value)}
                                                    placeholder="X-API-Key"
                                                    className="w-full bg-gray-50 dark:bg-[#0D1117] border border-gray-300 dark:border-gray-800 rounded-xl pl-9 pr-3 py-2.5 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500/50 outline-none transition-all"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                                API Key Value <span className="text-orange-500">*</span>
                                            </label>
                                            <div className="relative group">
                                                <Key className="absolute left-3.5 top-3 h-3.5 w-3.5 text-gray-400 dark:text-gray-500 group-focus-within:text-orange-500 transition-colors" />
                                                <input
                                                    type={showApiKey ? "text" : "password"}
                                                    disabled={integrationData?.editable === false}
                                                    value={authHeaderValue}
                                                    onChange={(e) => setAuthHeaderValue(e.target.value)}
                                                    autoComplete="new-password"
                                                    placeholder="Enter API key"
                                                    className="w-full bg-gray-50 dark:bg-[#0D1117] border border-gray-300 dark:border-gray-800 rounded-xl pl-9 pr-10 py-2.5 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500/50 outline-none transition-all"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowApiKey(!showApiKey)}
                                                    className="absolute right-3 top-2.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-0.5"
                                                >
                                                    {showApiKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5 text-gray-400" />}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {authType === "basic" && (
                                <div className="p-3.5 bg-gray-50/80 dark:bg-[#0D1117]/60 border border-gray-200 dark:border-gray-800/80 rounded-xl space-y-3">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                            <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                                Username <span className="text-orange-500">*</span>
                                            </label>
                                            <div className="relative group">
                                                <Lock className="absolute left-3.5 top-3 h-3.5 w-3.5 text-gray-400 dark:text-gray-500 group-focus-within:text-orange-500 transition-colors" />
                                                <input
                                                    type="text"
                                                    disabled={integrationData?.editable === false}
                                                    value={basicUsername}
                                                    onChange={(e) => setBasicUsername(e.target.value)}
                                                    placeholder="Username"
                                                    className="w-full bg-gray-50 dark:bg-[#0D1117] border border-gray-300 dark:border-gray-800 rounded-xl pl-9 pr-3 py-2.5 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500/50 outline-none transition-all"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                                Password <span className="text-orange-500">*</span>
                                            </label>
                                            <div className="relative group">
                                                <Lock className="absolute left-3.5 top-3 h-3.5 w-3.5 text-gray-400 dark:text-gray-500 group-focus-within:text-orange-500 transition-colors" />
                                                <input
                                                    type={showBasicPassword ? "text" : "password"}
                                                    disabled={integrationData?.editable === false}
                                                    value={basicPassword}
                                                    onChange={(e) => setBasicPassword(e.target.value)}
                                                    autoComplete="new-password"
                                                    placeholder="Password"
                                                    className="w-full bg-gray-50 dark:bg-[#0D1117] border border-gray-300 dark:border-gray-800 rounded-xl pl-9 pr-10 py-2.5 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500/50 outline-none transition-all"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowBasicPassword(!showBasicPassword)}
                                                    className="absolute right-3 top-2.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-0.5"
                                                >
                                                    {showBasicPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5 text-gray-400" />}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {authType === "oauth2_client_credentials" && (
                                <div className="p-3.5 bg-gray-50/80 dark:bg-[#0D1117]/60 border border-gray-200 dark:border-gray-800/80 rounded-xl space-y-3">
                                    <div>
                                        <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                            Token Endpoint URL <span className="text-orange-500">*</span>
                                        </label>
                                        <div className="relative group">
                                            <Globe className="absolute left-3.5 top-3 h-3.5 w-3.5 text-gray-400 dark:text-gray-500 group-focus-within:text-orange-500 transition-colors" />
                                            <input
                                                type="url"
                                                disabled={integrationData?.editable === false}
                                                value={tokenUrl}
                                                onChange={(e) => setTokenUrl(e.target.value)}
                                                placeholder="https://auth.example.com/oauth/v2/token"
                                                className="w-full bg-gray-50 dark:bg-[#0D1117] border border-gray-300 dark:border-gray-800 rounded-xl pl-9 pr-3 py-2.5 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500/50 outline-none transition-all"
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                            <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                                Client ID <span className="text-orange-500">*</span>
                                            </label>
                                            <div className="relative group">
                                                <Key className="absolute left-3.5 top-3 h-3.5 w-3.5 text-gray-400 dark:text-gray-500 group-focus-within:text-orange-500 transition-colors" />
                                                <input
                                                    type="text"
                                                    disabled={integrationData?.editable === false}
                                                    value={clientId}
                                                    onChange={(e) => setClientId(e.target.value)}
                                                    placeholder="OAuth2 Client ID"
                                                    className="w-full bg-gray-50 dark:bg-[#0D1117] border border-gray-300 dark:border-gray-800 rounded-xl pl-9 pr-3 py-2.5 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500/50 outline-none transition-all"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                                Client Secret <span className="text-orange-500">*</span>
                                            </label>
                                            <div className="relative group">
                                                <Key className="absolute left-3.5 top-3 h-3.5 w-3.5 text-gray-400 dark:text-gray-500 group-focus-within:text-orange-500 transition-colors" />
                                                <input
                                                    type={showClientSecret ? "text" : "password"}
                                                    disabled={integrationData?.editable === false}
                                                    value={clientSecret}
                                                    onChange={(e) => setClientSecret(e.target.value)}
                                                    autoComplete="new-password"
                                                    placeholder="OAuth2 Client Secret"
                                                    className="w-full bg-gray-50 dark:bg-[#0D1117] border border-gray-300 dark:border-gray-800 rounded-xl pl-9 pr-10 py-2.5 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500/50 outline-none transition-all"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowClientSecret(!showClientSecret)}
                                                    className="absolute right-3 top-2.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-0.5"
                                                >
                                                    {showClientSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5 text-gray-400" />}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                            Scope <span className="text-gray-400 dark:text-gray-500 font-normal">(Optional)</span>
                                        </label>
                                        <input
                                            type="text"
                                            disabled={integrationData?.editable === false}
                                            value={scope}
                                            onChange={(e) => setScope(e.target.value)}
                                            placeholder="e.g. read:events write:alerts"
                                            className="w-full bg-gray-50 dark:bg-[#0D1117] border border-gray-300 dark:border-gray-800 rounded-xl px-3.5 py-2.5 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500/50 outline-none transition-all"
                                        />
                                    </div>

                                    {/* Advanced OAuth2 Toggle */}
                                    <div className="pt-1">
                                        <button
                                            type="button"
                                            onClick={() => setShowAdvancedOauth(!showAdvancedOauth)}
                                            className="text-xs text-orange-600 dark:text-orange-400 hover:text-orange-500 dark:hover:text-orange-300 font-semibold flex items-center gap-1.5 cursor-pointer py-1"
                                        >
                                            <Sliders className="w-3 h-3" />
                                            <span>{showAdvancedOauth ? "Hide Advanced Token Options" : "Show Advanced Token Options"}</span>
                                            <ChevronDown className={`w-3 h-3 transition-transform ${showAdvancedOauth ? 'rotate-180' : ''}`} />
                                        </button>

                                        {showAdvancedOauth && (
                                            <div className="mt-2.5 pt-2.5 border-t border-gray-200 dark:border-gray-800/80 space-y-3">
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                    <CustomDropdown
                                                        label="Token Request Method"
                                                        value={tokenAuthMethod}
                                                        onChange={setTokenAuthMethod}
                                                        options={TOKEN_AUTH_METHODS}
                                                        disabled={integrationData?.editable === false}
                                                    />
                                                    <CustomDropdown
                                                        label="Token Injection"
                                                        value={tokenPlacement}
                                                        onChange={setTokenPlacement}
                                                        options={TOKEN_PLACEMENTS}
                                                        disabled={integrationData?.editable === false}
                                                    />
                                                </div>

                                                {tokenPlacement === "header" ? (
                                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                        <div>
                                                            <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                                                Token Header Name
                                                            </label>
                                                            <input
                                                                type="text"
                                                                disabled={integrationData?.editable === false}
                                                                value={tokenHeaderName}
                                                                onChange={(e) => setTokenHeaderName(e.target.value)}
                                                                placeholder="Authorization"
                                                                className="w-full bg-gray-50 dark:bg-[#0D1117] border border-gray-300 dark:border-gray-800 rounded-xl px-3.5 py-2.5 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500/50 outline-none transition-all"
                                                            />
                                                        </div>
                                                        <div>
                                                            <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                                                Token Format / Prefix
                                                            </label>
                                                            <input
                                                                type="text"
                                                                disabled={integrationData?.editable === false}
                                                                value={tokenFormat}
                                                                onChange={(e) => setTokenFormat(e.target.value)}
                                                                placeholder="Bearer "
                                                                className="w-full bg-gray-50 dark:bg-[#0D1117] border border-gray-300 dark:border-gray-800 rounded-xl px-3.5 py-2.5 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500/50 outline-none transition-all"
                                                            />
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                                            Token Query Parameter Name
                                                        </label>
                                                        <input
                                                            type="text"
                                                            disabled={integrationData?.editable === false}
                                                            value={tokenQueryParam}
                                                            onChange={(e) => setTokenQueryParam(e.target.value)}
                                                            placeholder="access_token"
                                                            className="w-full bg-gray-50 dark:bg-[#0D1117] border border-gray-300 dark:border-gray-800 rounded-xl px-3.5 py-2.5 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500/50 outline-none transition-all"
                                                        />
                                                    </div>
                                                )}

                                                <div>
                                                    <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                                        Token JSON Dot-Path <span className="text-gray-400 dark:text-gray-500 font-normal">(Leave empty for auto-detect)</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        disabled={integrationData?.editable === false}
                                                        value={tokenResponsePath}
                                                        onChange={(e) => setTokenResponsePath(e.target.value)}
                                                        placeholder="access_token (e.g. data.token or jwt)"
                                                        className="w-full bg-gray-50 dark:bg-[#0D1117] border border-gray-300 dark:border-gray-800 rounded-xl px-3.5 py-2.5 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500/50 outline-none transition-all"
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Custom Headers Accordion */}
                            <div className="border border-gray-200 dark:border-gray-800 rounded-xl bg-gray-50/80 dark:bg-[#0D1117]/40 overflow-hidden">
                                <div className="px-4 py-3 flex items-center justify-between">
                                    <button
                                        type="button"
                                        onClick={() => setShowCustomHeaders(!showCustomHeaders)}
                                        className="flex items-center gap-2 text-xs font-bold text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white cursor-pointer"
                                    >
                                        <ChevronDown className={`w-3.5 h-3.5 text-gray-400 dark:text-gray-400 transition-transform ${showCustomHeaders ? 'rotate-180' : ''}`} />
                                        <span>Custom HTTP Headers</span>
                                        {customHeaders.length > 0 && (
                                            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-500/20">
                                                {customHeaders.length}
                                            </span>
                                        )}
                                    </button>

                                    <button
                                        type="button"
                                        disabled={integrationData?.editable === false}
                                        onClick={handleAddHeader}
                                        className="flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400 hover:text-orange-500 dark:hover:text-orange-300 font-semibold cursor-pointer disabled:opacity-50"
                                    >
                                        <Plus className="w-3.5 h-3.5" />
                                        <span>Add Header</span>
                                    </button>
                                </div>

                                {showCustomHeaders && (
                                    <div className="px-4 pb-3.5 border-t border-gray-200 dark:border-gray-800/80 pt-3 space-y-2">
                                        {customHeaders.length === 0 ? (
                                            <p className="text-[11px] text-gray-500 italic">No custom headers configured.</p>
                                        ) : (
                                            customHeaders.map((header) => (
                                                <div key={header.id} className="flex items-center gap-2">
                                                    <input
                                                        type="text"
                                                        disabled={integrationData?.editable === false}
                                                        value={header.key}
                                                        onChange={(e) => handleHeaderChange(header.id, "key", e.target.value)}
                                                        placeholder="Header (e.g. X-Tenant-ID)"
                                                        className="flex-1 bg-white dark:bg-[#0D1117] border border-gray-300 dark:border-gray-800 rounded-lg px-3 py-2 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500/50 outline-none transition-all"
                                                    />
                                                    <input
                                                        type="text"
                                                        disabled={integrationData?.editable === false}
                                                        value={header.value}
                                                        onChange={(e) => handleHeaderChange(header.id, "value", e.target.value)}
                                                        placeholder="Value (e.g. prod-1)"
                                                        className="flex-1 bg-white dark:bg-[#0D1117] border border-gray-300 dark:border-gray-800 rounded-lg px-3 py-2 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500/50 outline-none transition-all"
                                                    />
                                                    <button
                                                        type="button"
                                                        disabled={integrationData?.editable === false}
                                                        onClick={() => handleRemoveHeader(header.id)}
                                                        className="text-gray-400 dark:text-gray-500 hover:text-rose-600 dark:hover:text-rose-400 p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                                                        title="Remove Header"
                                                    >
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>

                            {/* HMAC Payload Signing */}
                            <div className="border border-gray-200 dark:border-gray-800 rounded-xl bg-gray-50/80 dark:bg-[#0D1117]/40 p-4 space-y-3">
                                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        disabled={integrationData?.editable === false}
                                        checked={enableHmac}
                                        onChange={(e) => setEnableHmac(e.target.checked)}
                                        className="rounded border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-orange-500 focus:ring-orange-500/30 w-4 h-4 cursor-pointer"
                                    />
                                    <span className="text-xs font-bold text-gray-800 dark:text-gray-200">
                                        Enable HMAC Payload Signing <span className="text-gray-400 dark:text-gray-500 font-normal">(Optional)</span>
                                    </span>
                                </label>

                                {enableHmac && (
                                    <div className="pt-2 border-t border-gray-200 dark:border-gray-800/80 space-y-3">
                                        <div>
                                            <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                                Signing Secret <span className="text-orange-500">*</span>
                                            </label>
                                            <div className="relative group">
                                                <Key className="absolute left-3.5 top-3 h-3.5 w-3.5 text-gray-400 dark:text-gray-500 group-focus-within:text-orange-500 transition-colors" />
                                                <input
                                                    type={showHmacSecret ? "text" : "password"}
                                                    disabled={integrationData?.editable === false}
                                                    value={secretToken}
                                                    onChange={(e) => setSecretToken(e.target.value)}
                                                    autoComplete="new-password"
                                                    placeholder="Shared HMAC secret key"
                                                    className="w-full bg-gray-50 dark:bg-[#0D1117] border border-gray-300 dark:border-gray-800 rounded-xl pl-9 pr-10 py-2.5 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500/50 outline-none transition-all"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowHmacSecret(!showHmacSecret)}
                                                    className="absolute right-3 top-2.5 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-0.5"
                                                >
                                                    {showHmacSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5 text-gray-400" />}
                                                </button>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            <div>
                                                <label className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                                    Signature Header Name
                                                </label>
                                                <input
                                                    type="text"
                                                    disabled={integrationData?.editable === false}
                                                    value={signatureHeader}
                                                    onChange={(e) => setSignatureHeader(e.target.value)}
                                                    placeholder="X-Webhook-Signature"
                                                    className="w-full bg-gray-50 dark:bg-[#0D1117] border border-gray-300 dark:border-gray-800 rounded-xl px-3.5 py-2.5 text-xs text-gray-900 dark:text-gray-200 placeholder-gray-400 dark:placeholder-gray-500 focus:border-orange-500/50 outline-none transition-all"
                                                />
                                            </div>

                                            <CustomDropdown
                                                label="Signature Format"
                                                value={signatureFormat}
                                                onChange={setSignatureFormat}
                                                options={SIGNATURE_FORMATS}
                                                disabled={integrationData?.editable === false}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* TLS Certificate Option */}
                            <div className="p-3 bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800/80 rounded-xl">
                                <label className="flex items-center gap-2.5 cursor-pointer select-none">
                                    <input
                                        type="checkbox"
                                        disabled={integrationData?.editable === false}
                                        checked={insecureSkipVerify}
                                        onChange={(e) => setInsecureSkipVerify(e.target.checked)}
                                        className="rounded border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-orange-500 focus:ring-orange-500/30 w-4 h-4 cursor-pointer"
                                    />
                                    <div>
                                        <span className="text-xs font-semibold text-gray-800 dark:text-gray-300 block">
                                            Skip TLS Certificate Verification
                                        </span>
                                        <span className="text-[11px] text-gray-500 block">
                                            Allow self-signed or internal SSL/TLS certificates (insecure_skip_verify)
                                        </span>
                                    </div>
                                </label>
                            </div>
                        </div>
                    ) : (
                        /* Test Connection Tab */
                        <div className="space-y-4">
                            {/* Summary Card */}
                            <div className="p-4 bg-gray-50 dark:bg-[#0D1117] border border-gray-200 dark:border-gray-800 rounded-xl space-y-3">
                                <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-800/80 pb-2.5">
                                    <div className="flex items-center gap-2">
                                        <Globe className="w-4 h-4 text-orange-500 dark:text-orange-400" />
                                        <span className="text-xs font-bold text-gray-900 dark:text-gray-200">Target Webhook Endpoint</span>
                                    </div>
                                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-orange-500/10 border border-orange-500/20 text-orange-600 dark:text-orange-400">
                                        {httpMethod}
                                    </span>
                                </div>
                                <div className="text-xs font-mono text-gray-800 dark:text-gray-300 break-all bg-white dark:bg-gray-950/60 p-2.5 rounded-lg border border-gray-200 dark:border-gray-900">
                                    {webhookUrl || "(No URL entered)"}
                                </div>

                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 pt-1 text-xs">
                                    <div className="bg-white dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800/60 p-2 rounded-lg">
                                        <span className="text-[10px] text-gray-500 uppercase block font-bold">Auth Method</span>
                                        <span className="text-gray-800 dark:text-gray-300 font-semibold truncate block">
                                            {AUTH_TYPE_OPTIONS.find(a => a.value === authType)?.label || authType}
                                        </span>
                                    </div>
                                    <div className="bg-white dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800/60 p-2 rounded-lg">
                                        <span className="text-[10px] text-gray-500 uppercase block font-bold">HMAC Signing</span>
                                        <span className="text-gray-800 dark:text-gray-300 font-semibold block">
                                            {enableHmac ? `Enabled (${signatureFormat})` : 'Disabled'}
                                        </span>
                                    </div>
                                    <div className="bg-white dark:bg-gray-900/40 border border-gray-200 dark:border-gray-800/60 p-2 rounded-lg col-span-2 sm:col-span-1">
                                        <span className="text-[10px] text-gray-500 uppercase block font-bold">Custom Headers</span>
                                        <span className="text-gray-800 dark:text-gray-300 font-semibold block">
                                            {customHeaders.filter(h => h.key.trim()).length} header(s)
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* Runner & Status Box */}
                            <div className="p-4 bg-gray-50 dark:bg-[#0D1117] border border-gray-200 dark:border-gray-800 rounded-xl space-y-4">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-bold text-gray-900 dark:text-gray-200 flex items-center gap-2">
                                        <Send className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
                                        Live Connectivity Test
                                    </span>
                                    <button
                                        type="button"
                                        disabled={isTesting}
                                        onClick={executeTestConnection}
                                        className="px-3 py-1.5 bg-orange-500/10 hover:bg-orange-500/20 text-orange-600 dark:text-orange-400 border border-orange-500/30 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                                    >
                                        {isTesting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                        {isTesting ? "Testing..." : "Re-run Test"}
                                    </button>
                                </div>

                                {isTesting && (
                                    <div className="p-6 text-center space-y-2 bg-white dark:bg-gray-950/40 rounded-xl border border-gray-200 dark:border-gray-900">
                                        <Loader2 className="w-6 h-6 animate-spin text-orange-500 mx-auto" />
                                        <p className="text-xs text-gray-700 dark:text-gray-300 font-medium">Sending test webhook ping...</p>
                                        <p className="text-[11px] text-gray-500">Connecting to target server and verifying authentication</p>
                                    </div>
                                )}

                                {!isTesting && testResult && (
                                    <div className={`p-4 rounded-xl border space-y-3 animate-in fade-in duration-200 ${testResult.success
                                        ? 'bg-emerald-500/10 dark:bg-emerald-950/20 border-emerald-500/30 text-emerald-800 dark:text-emerald-300'
                                        : 'bg-rose-500/10 dark:bg-rose-950/20 border-rose-500/30 text-rose-800 dark:text-rose-300'
                                        }`}>
                                        <div className="flex items-center justify-between flex-wrap gap-2">
                                            <div className="flex items-center gap-2">
                                                {testResult.success ? (
                                                    <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400 shrink-0" />
                                                ) : (
                                                    <AlertCircle className="w-4 h-4 text-rose-600 dark:text-rose-400 shrink-0" />
                                                )}
                                                <span className={`text-xs font-bold ${testResult.success ? 'text-emerald-700 dark:text-emerald-300' : 'text-rose-700 dark:text-rose-300'}`}>
                                                    {testResult.success ? "Connection Verified" : "Connection Failed"}
                                                </span>
                                            </div>

                                            <div className="flex items-center gap-2">
                                                {testResult.statusCode && (
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold border ${testResult.statusCode >= 200 && testResult.statusCode < 300
                                                        ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-400'
                                                        : 'bg-rose-500/10 border-rose-500/30 text-rose-700 dark:text-rose-400'
                                                        }`}>
                                                        HTTP {testResult.statusCode}
                                                    </span>
                                                )}
                                                {testResult.durationMs !== undefined && (
                                                    <span className="flex items-center gap-1 text-[10px] text-gray-500 dark:text-gray-400 font-mono">
                                                        <Clock className="w-3 h-3" />
                                                        {testResult.durationMs}ms
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        <p className="text-xs text-gray-700 dark:text-gray-300 leading-relaxed break-words">
                                            {testResult.message}
                                        </p>

                                        {testResult.responsePreview && (
                                            <div className="space-y-1.5 pt-1">
                                                <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest block">
                                                    Response Preview
                                                </span>
                                                <pre className="text-[11px] font-mono text-gray-800 dark:text-gray-300 bg-white dark:bg-gray-950 p-3 rounded-lg border border-gray-200 dark:border-gray-800 overflow-x-auto max-h-40 whitespace-pre-wrap break-all">
                                                    {testResult.responsePreview}
                                                </pre>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer Actions */}
                <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-800/80 bg-gray-50/50 dark:bg-[#0F141F] flex items-center justify-between shrink-0">
                    <div>
                        {activeTab === "test" && (
                            <button
                                type="button"
                                onClick={() => setActiveTab("configuration")}
                                className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer px-2 py-1.5"
                            >
                                <ArrowLeft className="w-3.5 h-3.5" />
                                <span>Back to Configuration</span>
                            </button>
                        )}
                    </div>

                    <div className="flex items-center gap-3">
                        {integrationData?.editable === false ? (
                            <button
                                type="button"
                                onClick={onClose}
                                className="px-5 py-2.5 bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 text-gray-800 dark:text-white rounded-xl text-xs font-bold transition-all cursor-pointer"
                            >
                                Close
                            </button>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="px-4 py-2.5 border border-gray-300 dark:border-gray-700/80 rounded-xl text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all cursor-pointer"
                                >
                                    Cancel
                                </button>

                                {activeTab === "configuration" ? (
                                    <>
                                        <button
                                            type="button"
                                            onClick={handleTriggerTestTab}
                                            disabled={isLoading}
                                            className="px-4 py-2.5 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 text-orange-600 dark:text-orange-300 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shadow-sm"
                                        >
                                            <Send className="w-3.5 h-3.5 text-orange-500 dark:text-orange-400" />
                                            <span>Test Connection</span>
                                        </button>

                                        <button
                                            type="button"
                                            onClick={handleSave}
                                            disabled={isLoading}
                                            className="px-5 py-2.5 bg-orange-600 hover:bg-orange-500 text-white rounded-xl text-xs font-bold shadow-lg shadow-orange-950/30 disabled:opacity-50 transition-all flex items-center gap-1.5 cursor-pointer"
                                        >
                                            {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                                            <span>{isEditing ? 'Save Changes' : 'Connect Webhook'}</span>
                                        </button>
                                    </>
                                ) : (
                                    /* Save directly from Test Connection tab */
                                    <button
                                        type="button"
                                        onClick={handleSave}
                                        disabled={isLoading}
                                        className="px-5 py-2.5 bg-orange-600 hover:bg-orange-500 text-white rounded-xl text-xs font-bold shadow-lg shadow-orange-950/30 disabled:opacity-50 transition-all flex items-center gap-1.5 cursor-pointer"
                                    >
                                        {isLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                                        <span>{isEditing ? 'Save Changes' : 'Connect Webhook'}</span>
                                    </button>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default WebhookIntegrationModal;
